import React from 'react';
import { User, Shield, Trash2, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Button } from './Button';
import { Skeleton } from './Skeleton';
import { WorkflowParticipant } from '../types/workflow';
import styles from './PermissionsPanel.module.css';

interface WorkflowParticipantsTabProps {
  workflowId: string;
  participants: (WorkflowParticipant & {
    email: string;
    displayName: string;
    avatarUrl?: string;
  })[];
  isLoading: boolean;
  onUpdatePermission?: (participantId: string, permissionLevel: string) => void;
  onRemoveParticipant?: (participantId: string) => void;
}

export const WorkflowParticipantsTab = ({
  workflowId,
  participants,
  isLoading,
  onUpdatePermission,
  onRemoveParticipant,
}: WorkflowParticipantsTabProps) => {
  const handlePermissionChange = (participantId: string, level: string) => {
    onUpdatePermission?.(participantId, level);
  };

  const handleRemoveParticipant = (participantId: string) => {
    onRemoveParticipant?.(participantId);
  };

  if (isLoading) {
    return (
      <div className={styles.listContainer}>
        {[1, 2, 3].map((i) => (
          <div key={i} className={styles.listItem}>
            <Skeleton className={styles.avatarSkeleton} />
            <div className={styles.infoSkeleton}>
              <Skeleton style={{ width: '120px', height: '1rem' }} />
              <Skeleton style={{ width: '180px', height: '0.8rem' }} />
            </div>
            <Skeleton style={{ width: '100px', height: '2rem' }} />
          </div>
        ))}
      </div>
    );
  }

  if (participants.length === 0) {
    return (
      <div className={styles.emptyState}>
        <User size={32} className={styles.emptyIcon} />
        <p>No participants yet.</p>
        <p>Invite collaborators to help build and manage this workflow.</p>
      </div>
    );
  }

  return (
    <div className={styles.listContainer}>
      {participants.map((participant) => (
        <div key={participant.id} className={styles.listItem}>
          <div className={styles.userInfo}>
            <div className={styles.avatar}>
              {participant.avatarUrl ? (
                <img
                  src={participant.avatarUrl}
                  alt={participant.displayName}
                  crossOrigin="anonymous"
                />
              ) : (
                <span>{participant.displayName.substring(0, 2).toUpperCase()}</span>
              )}
            </div>
            <div className={styles.userDetails}>
              <span className={styles.userName}>{participant.displayName}</span>
              <span className={styles.userEmail}>{participant.email}</span>
            </div>
          </div>

          <div className={styles.actions}>
            <Select
              value={participant.permissionLevel}
              onValueChange={(val) => handlePermissionChange(participant.id, val)}
            >
              <SelectTrigger className={styles.permissionSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="view">View</SelectItem>
                <SelectItem value="edit">Edit</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRemoveParticipant(participant.id)}
              className={styles.removeButton}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
